import { useEffect, useState } from "react";
import IEditFolderProps from "./IEditFolderProps";
import Status from "../../enums/Status";
import IFolder from "../../models/IFolder";
import useStyleUtils from "../../utilities/styles";
import folderService from "../../services/folderService";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, MessageBar, Spinner, Tooltip } from "@fluentui/react-components";
import { Edit48Regular } from "@fluentui/react-icons";
import Error from "./Error";
import FolderForm from "../form/FolderForm";
import { useTranslation } from "react-i18next";
import fileService from "../../services/fileService";
import { fileToBase64 } from "../../utilities/files";
import { v4 as uuid } from "uuid";
import IBlob from "../../models/IBlob";
import OwnershipMessage from "../ownershipMessage/OwnershipMessage";
import Ownership from "../../enums/Ownership";

export default function EditFolder(props: IEditFolderProps) {
    const { t } = useTranslation();

    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState<Status>(Status.NEUTRAL);
    const [form, setForm] = useState<IFolder>(null!);
    const [image, setImage] = useState<File>(null!);
    
    const handleImageChange = (img: File) => setImage(img);

    const style = useStyleUtils();

    useEffect(() => {
        if (!open) {
            return;
        }

        setStatus(Status.LOADING);

        getFolder(props.folderId, setForm, setImage)
            .then(() => setStatus(Status.LOAD_SUCCESS))
            .catch(() => setStatus(Status.LOAD_ERROR));
    }, [open, props.folderId]);
    
    const handleChange = (key: string, value?: string) => setForm({ ...form, [key]: value });

    const handleSubmit = () => {
        setStatus(Status.SAVING);

        submit(form, image)
            .then((result: boolean) => {
                if (!result) {
                    setStatus(Status.SAVE_ERROR);
                    return;
                }

                setStatus(Status.SAVE_SUCCESS);
                props.onUpdate();
                setOpen(false);
            })
            .catch(error => console.error(error));
      };
    
    return (
        <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
                <Tooltip content={t("folders.titles.edit")} relationship='label'>
                    <Button icon={<Edit48Regular />} />
                </Tooltip>
            </DialogTrigger>
            <DialogSurface>
                <DialogTitle>{t("folders.titles.edit")}</DialogTitle>
                <DialogBody>
                    <DialogContent>
                        {
                            status === Status.LOADING &&
                            <div className={`${style.textCenter} ${style.w100}`}>
                                <Spinner />
                            </div>
                        }

                        {
                            status === Status.LOAD_ERROR &&
                            <Error />
                        }

                        {
                            [Status.LOAD_SUCCESS, Status.SAVING, Status.SAVE_ERROR].includes(status) &&
                            <>
                                <OwnershipMessage ownership={form.isPersonal ? Ownership.PERSONAL : Ownership.SHARED} isNew={false} />
                                <FolderForm data={form} image={image} onImageChange={handleImageChange} onChange={handleChange} />
                            </>
                        }
                        
                        {
                            status === Status.SAVE_ERROR &&
                            <MessageBar intent="error">{t("folders.errors.saveFolder")}</MessageBar>
                        }
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary">
                                {t("general.cancel")}
                            </Button>
                        </DialogTrigger>
                        <Button appearance="primary" onClick={handleSubmit}>
                            {
                                status === Status.SAVING && <Spinner size="extra-small" className={style.mr3} />
                            }

                            {t("general.save")}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

async function getFolder(folderId: string, setForm: (folder: IFolder) => void, setImage: (image: File) => void) {
    const folder = await folderService.getFolderById(folderId);
    const logo = folder?.logoUri
        ? await fileService.downloadFile(folder.logoUri)
        : null;
    
    setForm(folder!);
    setImage(logo!);
}

async function submit(folder: IFolder, image?: File) : Promise<boolean> {
    try {
        if (image) {
            const fileExtension = image.name.split('.').pop();
            const filename = `${uuid()}.${fileExtension}`;

            const blob: IBlob = {
                blobName: filename,
                containerName: 'logos'
            };

            const imgUrlResult = await fileService.getUploadUrl(blob);
            if (!imgUrlResult) {
                return false;
            }

            const imgBase64 = await fileToBase64(image, true);
            const uploadResult = await fileService.uploadFile(imgUrlResult.url!, imgBase64);
            if (!uploadResult) {
                return false;
            }

            folder.logoUri = imgUrlResult.url!.split('?')[0];
        } else {
            folder.logoUri = null!;
        }
        
        const result = await folderService.updateFolder(folder);
        if (result) {
            return true;
        } else {
            return false;
        }
    } catch (e) {
        console.error(e);
        return false;
    }
}